<template>
    <div>
        <div class="rounded-lg border border-gray-200 bg-white shadow-lg min-h-[116px] relative">
            <div
                class="h-[8px] rounded-tl-lg rounded-tr-lg shadow"
                :class="{
                    'bg-[#1EB26D]': isPopular,
                    'bg-[#108FE5]': !isPopular,
                }"
            />

            <v-form
                class="flex flex-1 flex-col pb-6 p-[17px] sm:p-[27px]"
                v-model="valid"
                @submit.prevent="onSubmit"
            >
                <div class="min-h-[80px]">
                    <p class="text-[22px] font-bold text-[#2C2C4F]">
                        {{ title }}
                    </p>

                    <p
                        class="text-[16px] mt-1 font-semibold leading-snug"
                        :class="{
                            'text-[#1EB26D]': isPopular,
                            'text-[#108FE5]': !isPopular,
                        }"
                    >
                        {{ description }}
                    </p>
                </div>

                <div
                    class="flex flex-1 justify-between pb-4"
                >
                    <div class="flex flex-col justify-center flex-1 pr-2">
                        <QuoteCheckListItem
                            v-for="(item, index) in checklist"
                            :key="`quote-check-list-item-${index}`"
                            :text-content="item.textContent"
                            :tooltip-content="item.tooltipContent"
                        />
                    </div>

                    <div class="w-[125px] min-[430px]:w-[150px] min-h-[210px] mt-4 flex flex-shrink-0">
                        <div class="w-full h-full max-h-[210px] relative">
                            <img
                                :src="ensureImageSrcSlash(pvOffer.$data.solarPanelSelected.image_url)"
                                class="h-[90%] object-cover"
                                alt="Solar panel image"
                            />

                            <img
                                :src="ensureImageSrcSlash(pvOffer.$data.inverterSelected.image_url)"
                                class="w-[80px] h-[80px] absolute left-0 bottom-0 object-contain"
                                alt="Inverter image"
                            />
                        </div>
                    </div>
                </div>

                <div class="flex items-center justify-between">
                    <p
                        class="leading-7"
                        :class="{
                            'text-[#1EB26D]': isPopular,
                            'text-[#108FE5]': !isPopular,
                        }"
                    >
                        <span v-if="locale === 'nl'" class="text-[20px] font-medium pr-1">€</span>

                        <span class="text-[26px] sm:text-[34px] font-bold">
                            {{ formattedPrice.value }}
                        </span>

                        <span v-if="locale !== 'nl'" class="text-[17px] sm:text-[20px]">
                            {{ formattedPrice.suffix }}
                        </span>

                        <span v-if="discount > 0" class="text-[14px] font-medium pl-1.5">
                            {{ $t('campaign_version_one.quote_price_before_discount', {
                                    amount: $numberHelper.formatMoneyObject(pvOffer.ownInvestmentTotal(0)).value
                                    }
                                )
                            }}{{ formattedPrice.suffix }}
                        </span>
                    </p>

                    <button
                        @click="showDetails = !showDetails"
                        type="button"
                        class="rounded-full bg-[#F4F9FF] hover:bg-[#E7ECF2] px-3 py-1.5 text-[12px] flex items-center ml-2"
                    >
                        <span class="text-[#108FE5]">
                            {{ $t("campaign_version_one.quote_details_btn_label") }}
                        </span>

                        <img
                            src="/images/campaigns/down-arrow-quote-details-btn.svg"
                            class="ml-2 duration-200"
                            :class="{
                                'transform rotate-180': showDetails,
                            }"
                            alt="Down arrow icon"
                        />
                    </button>
                </div>

                <!-- DETAILS AND OPTIONS -->
                <div v-if="showDetails" class="mt-6 transition duration-500 overflow-hidden">
                    <template v-if="pvOffer.areOptimizersAvailable() || pvOffer.isRoofSheathingKeyAvailable('ground')">
                        <div class="w-full h-[2px] bg-[#CFD6EA]"></div>
                        <p class="text-[18px] pt-3 font-bold leading-7 text-[#141430]">
                            {{ $t('campaign_version_one.Options and add-ons') }}
                        </p>
                    </template>

                    <details-add-ons-item
                        v-if="pvOffer.areOptimizersAvailable()"
                        v-model="pvOffer.$data.enablePowerOptimizers"
                        :name="$t('campaign_version_one.Power Optimizers')"
                        :tooltip-content="$t('campaign_version_one.power_optimizers_tooltip_content')"
                        :price-value="pvOffer.getTotalPriceDiffToOptimizers()"
                        class="mt-2"
                    />

                    <details-add-ons-item
                        v-if="pvOffer.isRoofSheathingKeyAvailable('ground')"
                        v-model="pvOffer.$data.groundMounted"
                        :name="$t('campaign_version_one.Ground mounted')"
                        :tooltip-content="$t('campaign_version_one.ground_mounted_tooltip_content')"
                        :price-value="pvOffer.getPriceDiffToDefault('ground')"
                        class="mt-2"
                    />

                    <div class="w-full h-[2px] mt-5 bg-[#CFD6EA]"></div>

                    <p class="text-[18px] pt-3 font-bold leading-7 text-[#141430]">
                        {{ $t('campaign_version_one.Price Details') }}
                    </p>

                    <price-details-item
                        :name="$t('campaign_version_one.System price')"
                        :price-value="discount > 0 ? pvOffer.totalPriceNet() + discount : pvOffer.totalPriceNet()"
                        class="mt-2"
                    />

                    <price-details-item
                        v-if="discount > 0"
                        :name="$t('campaign_version_one.Discount')"
                        :price-value="-1 * discount"
                        colored-price
                        class="mt-1"
                    />

                    <price-details-item
                        v-if="discount > 0"
                        :name="$t('campaign_version_one.Price after discount')"
                        :price-value="pvOffer.totalPriceNet()"
                        class="mt-1"
                    />

                    <price-details-item
                        :name="`${$t('campaign_version_one.VAT')} (${vatPercentage}%)`"
                        :price-value="pvOffer.getVatAmount()"
                        class="mt-1"
                    />

                    <div class="w-full h-[2px] mt-3 bg-[#CFD6EA]"></div>

                    <price-details-item
                        :name="$t('campaign_version_one.Total')"
                        :price-value="pvOffer.totalPriceWithMarginWithVAT()"
                        emphasized
                        class="mt-2"
                    />

                    <price-details-item
                        v-if="pvOffer.getSubsidyAmount() > 0"
                        :name="$t('campaign_version_one.Subsidy solar panels')"
                        :tooltip-content="$t('campaign_version_one.subsidy_solar_panels_tooltip_content')"
                        :price-value="-1 * pvOffer.getSubsidyAmount()"
                        colored-price
                        class="mt-1"
                    />

                    <div v-if="pvOffer.getSubsidyAmount() > 0" class="w-full h-[2px] mt-3 bg-[#CFD6EA]"></div>

                    <price-details-item
                        v-if="pvOffer.getSubsidyAmount() > 0"
                        :name="$t('campaign_version_one.Your investment')"
                        :price-value="pvOffer.ownInvestmentTotal()"
                        emphasized
                        class="mt-2"
                    />
                </div>

                <!-- FORM -->
                <div v-if="step === 2" class="mt-6">
                    <div class="w-full h-[2px] bg-[#CFD6EA]"></div>

                    <p class="text-[22px] font-bold leading-loose text-[#2C2C4F] pt-3">
                        {{ $t("campaign_version_one.Your quote is ready!") }}
                    </p>

                    <p class="text-[14px] leading-snug text-[#2C2C4F]">
                        {{ $t("campaign_version_one.quote_subtitle") }}
                    </p>

                    <v-text-field
                        v-model="fullName"
                        :label="$t('campaign_version_one.Full name')"
                        :placeholder="$t('campaign_version_one.full_name_placeholder')"
                        variant="outlined"
                        density="comfortable"
                        class="mt-5"
                        :class="{'success': showValidationErrors && requiredRule(fullName) && fullNameRule(fullName)}"
                        persistent-placeholder
                        :rules="[rules.required, rules.fullName]">
                        <template v-slot:append-inner>
                            <v-fade-transition leave-absolute>
                                <div
                                    v-if="showValidationErrors && requiredRule(fullName) && fullNameRule(fullName)"
                                    class="w-[23px] h-[23px]"
                                >
                                    <img src="/images/campaigns/valid-field.svg" alt="valid field"/>
                                </div>
                            </v-fade-transition>
                        </template>
                    </v-text-field>

                    <v-text-field
                        v-model="phoneNumber"
                        :label="$t('campaign_version_one.Phone number')"
                        :placeholder="$t('campaign_version_one.phone_number_placeholder')"
                        variant="outlined"
                        density="comfortable"
                        class="mt-3"
                        :class="{'success': showValidationErrors && requiredRule(phoneNumber) && phoneNumberRule(phoneNumber)}"
                        persistent-placeholder
                        :rules="[rules.required, rules.phoneNumber]">
                        <template v-slot:append-inner>
                            <v-fade-transition leave-absolute>
                                <div
                                    v-if="showValidationErrors && requiredRule(phoneNumber) && phoneNumberRule(phoneNumber)"
                                    class="w-[23px] h-[23px]"
                                >
                                    <img src="/images/campaigns/valid-field.svg" />
                                </div>
                            </v-fade-transition>
                        </template>
                    </v-text-field>

                    <v-text-field
                        v-model="email"
                        :label="$t('campaign_version_one.Email')"
                        :placeholder="$t('campaign_version_one.email_placeholder')"
                        variant="outlined"
                        density="comfortable"
                        class="mt-3"
                        :class="{'success': showValidationErrors && requiredRule(email) && emailRule(email)}"
                        type="email"
                        persistent-placeholder
                        :rules="[rules.required, rules.email]">
                        <template v-slot:append-inner>
                            <v-fade-transition leave-absolute>
                                <div
                                    v-if="showValidationErrors && requiredRule(email) && emailRule(email)"
                                    class="w-[23px] h-[23px]"
                                >
                                    <img src="/images/campaigns/valid-field.svg" />
                                </div>
                            </v-fade-transition>
                        </template>
                    </v-text-field>

                    <v-combobox
                        v-if="locale === 'nl'"
                        v-model="address"
                        :items="autocompleteAddresses"
                        :label="$t('campaign_version_one.Address')"
                        :placeholder="$t('campaign_version_one.address_placeholder')"
                        variant="outlined"
                        density="comfortable"
                        class="mt-3"
                        :class="{'success': showValidationErrors && requiredRule(address) && addressRule(address)}"
                        persistent-placeholder
                        @input="addressPredictions()"
                        :rules="[rules.required, rules.address]">
                        <template v-slot:append-inner>
                            <v-fade-transition leave-absolute>
                                <div
                                    v-if="showValidationErrors && requiredRule(address) && addressRule(address)"
                                    class="w-[23px] h-[23px]"
                                >
                                    <img src="/images/campaigns/valid-field.svg" />
                                </div>
                            </v-fade-transition>
                        </template>
                    </v-combobox>

                    <v-text-field
                        v-else
                        v-model="address"
                        :label="$t('campaign_version_one.Address')"
                        :placeholder="$t('campaign_version_one.address_placeholder')"
                        variant="outlined"
                        density="comfortable"
                        class="mt-3"
                        :class="{'success': showValidationErrors && requiredRule(address) && addressRule(address)}"
                        persistent-placeholder
                        :rules="[rules.required, rules.address]">
                        <template v-slot:append-inner>
                            <v-fade-transition leave-absolute>
                                <div
                                    v-if="showValidationErrors && requiredRule(address) && addressRule(address)"
                                    class="w-[23px] h-[23px]"
                                >
                                    <img src="/images/campaigns/valid-field.svg" />
                                </div>
                            </v-fade-transition>
                        </template>
                    </v-text-field>
                </div>

                <!-- CONFIRMATION MESSAGE -->
                <div v-if="step === 3" class="mt-6">
                    <div class="w-full h-[2px] bg-[#CFD6EA]"></div>

                    <div class="py-24 flex flex-col justify-center items-center">
                        <img src="/images/campaigns/confirmation-icon.svg" alt="Confirmation icon">

                        <p class="text-[24px] pt-5 font-bold leading-loose text-center text-[#2C2C4F]">
                            {{ $t("campaign_version_one.Thank you") }}
                        </p>

                        <p class="text-[14px] max-w-[70%] leading-tight text-center text-[#959BB5]">
                            {{ $t("campaign_version_one.confirmation_message") }}
                        </p>
                    </div>
                </div>

                <button
                    v-if="step !== 3"
                    type="submit"
                    class="mt-6 mb-1 hover:bg-opacity-90 rounded-full px-4 py-2.5 text-center text-[15px] sm:text-[17px] font-semibold leading-snug text-white"
                    :class="{
                        'bg-[#1EB26D]': isPopular,
                        'bg-[#108FE5]': !isPopular,
                    }"
                >
                    <span v-if="step === 1">
                        {{ $t("campaign_version_one.quote_interested_button") }}
                    </span>

                    <span v-else>
                        {{ $t("campaign_version_one.Send me my offer") }}
                    </span>
                </button>
            </v-form>

            <div
                v-if="isPopular"
                class="absolute -top-1 -right-2 w-[116px] h-[116px]"
            >
                <img
                    src="/images/campaigns/popular-badge.svg"
                    class="w-full"
                    alt="Popular badge"
                />
            </div>
        </div>
    </div>
</template>

<script>
import QuoteCheckListItem from "@/Components/Campaigns/QuoteCheckListItem";
import DetailsAddOnsItem from "@/Components/Campaigns/DetailsAddOnsItem";
import PriceDetailsItem from "@/Components/Campaigns/PriceDetailsItem";
import PvOfferModel from "@/models/PvOfferModel";
import { reactive } from "vue";

export default {
    props: {
        isPopular: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        locale: {
            type: String,
            required: true,
        },
        quotePvModelData: {
            type: Object,
            required: true,
        },
        allInverters: {
            type: Array,
            required: true,
        },
        solarPanelId: {
            type: Number,
            required: true,
        },
        annualConsumption: {
            type: Number,
            required: true,
        },
        discount: {
            type: Number,
            required: false,
            default: () => 0,
        },
        userExtraPremium: {
            type: Number,
            required: false,
            default: () => 0,
        },
    },
    components: {
        QuoteCheckListItem,
        DetailsAddOnsItem,
        PriceDetailsItem,
    },
    setup(props) {
        const data = props.quotePvModelData

        const offerBasis = reactive({
            roof_sheathing: null,
            lightning_protection: false,
            grounding: false,
            digging: false,
            digging_length: 0,
            lift: false,
            stuck_tiles: false,
            snow_guards: false,
            lightning_rod_removal: false,
            insurance: false,
            imageUpload: null,
            clientName: null,
            clientAddress: null,
            pdId: null,
            vat_level: (props.locale && props.locale === 'pl') ? 1.08 : 1.06,
            // discountAvailable: false
        })

        let subsidy
        if (props.locale === 'nl') {
            subsidy = 'be_base'
        } else {
            subsidy = false;
        }

        const pvOffer = new PvOfferModel({
          defaultSolarPanelId: props.solarPanelId,
          defaultInverterId: null,
          roofSheathings: data.roofSheathings,
          priceFactors: props.priceFactors,
          userExtraPremium: props.userExtraPremium,
          offerBasis: offerBasis,
          samplePvOfferIndex: 1,
          allBatteries: data.allBatteries,
          allSolarPanels: data.allSolarPanels,
          allInverters: props.allInverters,
          minimumCompanyMargins: data.minimumCompanyMargins,
          baseMargins: data.baseMargins,
          annualConsumption: props.annualConsumption,
          dynamicConsumptionMode: true,
          subsidy: subsidy,
          enablePowerOptimizers: false,
          powerOptimizersAuto: true,
          groundMounted: false,
          discount: props.discount,
          autoFillOptimizers: true
        });

        return { pvOffer, offerBasis }
    },
    data() {
        return {
            valid: false,
            googleService: null,
            showDetails: false,
            step: 1,
            fullName: '',
            phoneNumber: '',
            email: '',
            address: '',
            autocompleteAddresses: [],
            rules: {
                required: value => this.requiredRule(value) || this.$t('campaign_version_one.required_field_rule'),
                fullName: value => this.fullNameRule(value) || this.$t('campaign_version_one.full_name_field_rule'),
                phoneNumber: value => this.phoneNumberRule(value) || this.$t('campaign_version_one.phone_number_field_rule'),
                email: value => this.emailRule(value) || this.$t('campaign_version_one.email_field_rule'),
                address: value => this.addressRule(value) || this.$t('campaign_version_one.address_field_rule'),
            },
            showValidationErrors: false,
        };
    },
    computed: {
        formattedPrice() {
            this.pvOffer.$data.annualConsumption = this.annualConsumption

            return this.$numberHelper.formatMoneyObject(this.pvOffer.ownInvestmentTotal())
        },
        checklist() {
            return [
                {
                    textContent: this.$t("campaign_version_one.quote_checklist_item_1", {
                        numberOfPanels: this.pvOffer.getPanelNumber(),
                        panelsBrand: this.pvOffer.$data.solarPanelSelected.brand,
                        panelsPower: this.pvOffer.$data.solarPanelSelected.power_output,
                        yearsWarranty: this.pvOffer.$data.solarPanelSelected.product_warranty_years
                    }),
                    tooltipContent: this.pvOffer.$data.solarPanelSelected.model,
                    // tooltipContent: this.$t("campaign_version_one.quote_checklist_item_1_tooltip"),
                },
                {
                    textContent: this.$t("campaign_version_one.quote_checklist_item_2", {
                        numberOfInverters: this.pvOffer.$data.inverterQuantity,
                        inverterBrand: this.pvOffer.$data.inverterSelected.brand,
                        yearsWarranty: this.pvOffer.$data.inverterSelected.warranty
                    }),
                    tooltipContent: this.pvOffer.$data.inverterSelected.model,
                    // tooltipContent: this.$t("campaign_version_one.quote_checklist_item_2_tooltip"),
                },
                {
                    textContent: this.$t("campaign_version_one.quote_checklist_item_3"),
                    tooltipContent: this.$t("campaign_version_one.quote_checklist_item_3_tooltip"),
                }
            ];
        },
        vatPercentage() {
            return parseInt((parseFloat(this.offerBasis.vat_level) - 1) * 100);
        }
    },
    methods: {
        requiredRule(value) {
            if (!this.showValidationErrors) {
                return true;
            }

            return !!value;
        },
        fullNameRule(value) {
            if (!this.showValidationErrors) {
                return true;
            }

            return /^[A-Za-z\s-]+$/.test(value);
        },
        phoneNumberRule(value) {
            if (!this.showValidationErrors) {
                return true;
            }

            return /^(\d[\s-]*){9,}$/.test(value);
        },
        emailRule(value) {
            if (!this.showValidationErrors) {
                return true;
            }

            return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value);
        },
        addressRule(value) {
            if (!this.showValidationErrors) {
                return true;
            }

            return value.length > 0 && value.length <= 60;
        },
        setGoogleService() {
            if (!this.googleService) {
                this.googleService = new window.google.maps.places.AutocompleteService();
            }
        },
        addressPredictions() {
            const request = {
                input: this.address,
                types: ['address'],
                componentRestrictions: {
                    country: 'be'
                },
            };

            this.googleService.getPlacePredictions(request, (predictions) => {
                this.autocompleteAddresses = predictions ?
                    predictions.map((prediction) => prediction.description) :
                    [];
            })
        },
        ensureImageSrcSlash(src) {
            return src[0] === "/" ? src : "/" + src;
        },
        onSubmit() {
            if (this.step === 1) {
                if (this.locale === 'nl') {
                    this.setGoogleService();
                }

                this.step = 2;
            } else if (this.step === 2) {
                this.showValidationErrors = true;

                if (
                    !this.requiredRule(this.fullName) ||
                    !this.fullNameRule(this.fullName) ||
                    !this.requiredRule(this.phoneNumber) ||
                    !this.phoneNumberRule(this.phoneNumber) ||
                    !this.requiredRule(this.email) ||
                    !this.emailRule(this.email) ||
                    !this.requiredRule(this.address) ||
                    !this.addressRule(this.address)
                ) {
                    return;
                }

                axios.post(
                    this.route("lp-one-submit-offer"),
                    {
                        full_name: this.fullName,
                        phone_number: this.phoneNumber,
                        email: this.email,
                        address: this.address,
                        offer: this.pvOffer.finalOfferData()
                    }
                ).then((response) => {
                    if (response.data.success) {
                        this.step = 3;

                        setTimeout(() => {
                            this.showDetails = false;

                            this.step = 1;
                        }, 5000);
                    }
                });
            } else {
                return;
            }
        },
    }
};
</script>

<style lang="scss" scoped>
:deep(.v-field--variant-outlined) {
    font-size: 14px !important;
    font-family: Poppins !important;
    border-color: #CFD6EA !important;
    color: #5a5a5a !important;
}

:deep(.v-field--focused) {
    outline-color:#959BB5 !important;;
    color: #5a5a5a !important;
}

:deep(.v-field-label) {
    font-weight: 600 !important;
    font-size: 14px !important;
}

:deep(.v-field--variant-outlined input::placeholder) {
    font-size: 14px !important;
    color: #959BB5 !important;
    opacity: 1 !important;
}

:deep(.v-field__append-inner > i) {
    display: none;
}

:deep(.success *) {
    border-color: #059954 !important;
    color: #059954 !important;
}

:deep(.success input),
:deep(.success .v-combobox__selection-text) {
    color: #5a5a5a !important;
}
</style>
